import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useState } from "react";

import { loginUser } from "@/services/userServices/authActions";
import NotificationAlertOptions from "@/layouts/Alerts";
import getErrorMessage, { hasDisplayableError } from "@/utils/getErrorMessage";
import { useNavigate } from "react-router-dom";
import LoginView from "./LoginView";

const LoginContainer = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [focusedEmail, setFocusedEmail] = useState(false);
  const [focusedPassword, setFocusedPassword] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const auth = useAppSelector((state) => state.auth);
  const errors = useAppSelector((state) => state.errors);

  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate("/auth/siteselector");
    }
  }, [auth, navigate]);

  // Kept separate from the redirect above, and depending on `errors` alone
  // (SCRUM-1432). While both lived in one effect with `[auth, errors, navigate]`,
  // any change of the `auth` object's identity re-ran it with the same error still
  // in the slice and raised a second toast for one failure — measured here as an
  // intermittent 2-instead-of-1 in login.ui.test.js, ~1 run in 5.
  //
  // Route through the shared normalizer rather than reading `errors.errors[0].msg`
  // directly. That shape is only what express-validator returns; a backend that
  // cannot reach MongoDB answers `{ status, checks:{ mongo } }`, which has no
  // errors array, so the old condition was false and the screen showed nothing at
  // all — the Sign-in button just looked inert.
  useEffect(() => {
    if (hasDisplayableError(errors)) {
      NotificationAlertOptions("error", getErrorMessage(errors));
    }
  }, [errors]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(loginUser({ email, password }));
  };

  return (
    <LoginView
      email={email}
      password={password}
      focusedEmail={focusedEmail}
      focusedPassword={focusedPassword}
      setFocusedEmail={setFocusedEmail}
      setFocusedPassword={setFocusedPassword}
      onChange={onChange}
      onSubmit={onSubmit}
    />
  );
};

export default LoginContainer;
